import axios from 'axios';
import i18n from '../../locales/i18n';
import {
  dispatchAppEvent,
  getAuthHeaders,
  storage,
} from '../../platform';
import {
  getConsentRequestPolicy,
  canRequestProtectedResource,
  inferConsentTrigger,
} from './consentPolicy';

const API_URL = import.meta.env.VITE_API_URL || '/api';

const api = axios.create({
  baseURL: API_URL,
  headers: { 'Content-Type': 'application/json' },
});

api.interceptors.request.use((config) => {
  config.headers = config.headers || {};
  Object.assign(config.headers, getAuthHeaders());

  const token = storage.get('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  const policy = getConsentRequestPolicy(config);
  if (!canRequestProtectedResource(policy)) {
    if (policy.kind === 'action') {
      dispatchAppEvent('app:consent-required', {
        missing: [],
        trigger: inferConsentTrigger(policy.path),
      });
    }
    const error = new axios.CanceledError('CONSENT_REQUIRED');
    error.code = 'CONSENT_REQUIRED_LOCAL';
    error.config = config;
    error.consentRequired = true;
    return Promise.reject(error);
  }

  return config;
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error?.response?.status;
    if (status === 402) {
      const body = error.response.data || {};
      dispatchAppEvent('app:insufficient-access', {
        message:
          body.error === 'insufficient credits' && body.need != null
            ? i18n.t('common.insufficientCredits', {
                need: body.need,
                have: body.have ?? 0,
              })
            : body.message || null,
      });
    }
    if (status === 403 && error?.response?.data?.error === 'CONSENT_REQUIRED') {
      dispatchAppEvent('app:consent-required', {
        missing: error.response.data.missing || [],
        trigger: inferConsentTrigger(error?.config?.url || ''),
      });
    }
    return Promise.reject(error);
  },
);

export const apiClient = {
  user: {
    getMe: (signal) => api.get('/user/me', { signal }),
    getStats: (signal) => api.get('/user/stats', { signal }),
    updateProfile: (data, signal) => api.patch('/user/profile', data, { signal }),
  },
  generate: {
    getPresets: (signal) => api.get('/generate/presets', { signal }),
    getBasePresets: (signal) => api.get('/generate/base-presets', { signal }),
    getPresetGroups: (signal) => api.get('/preset-groups', { signal }),
    createGeneration: (data, signal) => api.post('/generate', data, { signal }),
    getHistory: (params, signal) => api.get('/generate/history', { params, signal }),
    getGeneration: (id, signal) => api.get('/generate/' + id, { signal }),
  },
  upload: {
    reference: (formData) =>
      api.post('/upload/reference', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      }),
  },
  payment: {
    getPlans: (signal) => api.get('/payment/plans', { signal }),
    getCreditPackages: (signal) => api.get('/payment/credits/packages', { signal }),
    subscribe: (data, signal) => api.post('/payment/subscribe', data, { signal }),
    purchaseCredits: (data, signal) => api.post('/payment/credits/purchase', data, { signal }),
  },
  analytics: {
    track: (event, props = {}, source = 'mini_app') =>
      api.post('/analytics/event', { event, props, source }),
  },
};

export default api;
